/* KCH build 25JUL2026-b | kch-product-carousel.js */
/* ============================================================================
   Kitty Cat Habitat, 3D Product / Category Carousel
   Asset: kch-product-carousel.js   |   Concept theme (RoarTheme)
   Stage 3 of 3. Pairs with sections/kch-product-carousel.liquid and
   assets/kch-product-carousel.css.

   This is the wireframe motion engine, ported with the same tuned constants,
   adapted to enhance cards that Liquid has already rendered (rather than
   building them from data). It positions the coverflow, drives navigation by
   arrows, drag and keyboard, runs the hover image gallery, and upgrades
   the no-JS scroll row to the 3D stage. Quick-view and add-to-cart are left to
   Concept's own engine; this file only routes clicks to it.
   ============================================================================ */
(() => {
  'use strict';

  /* Two carousels on one page inject this script twice; run the document-level
     wiring once so the lifecycle listeners are not registered twice. */
  if (window.__kchCarouselLoaded) return;

  /* Focus intent, once per page and outside any section init. Earlier versions attached
     this inside the engine, which returns early in Concept mode, so Concept had no
     suppression at all: that is why the ring kept coming back. The flag lives on the
     document element, is set by any pointer press, and clears only on Tab, because Tab
     is the signal that someone is navigating by keyboard and needs to see focus. */
  if (!window.__kchFocusIntent) {
    window.__kchFocusIntent = true;
    document.addEventListener('pointerdown', function () {
      document.documentElement.setAttribute('data-kch-pointer', '');
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') document.documentElement.removeAttribute('data-kch-pointer');
    }, true);
  }

  window.__kchCarouselLoaded = true;


  /* ==========================================================================
     Mobile effect, scroll driven                                  25 Jul 2026
     --------------------------------------------------------------------------
     The card keeps the size concept gives it and the effect lives in the travel
     between cards, which is Sean's brief: on a phone you notice it as you scroll
     rather than seeing the whole fan at rest.

     This is the inverse of the desktop engine's control flow. Up there the fan
     owns position outright, pins scrollLeft to zero and places every card from
     sin(theta) * radius. Here native scroll owns position, so the browser keeps
     snap, momentum, overscroll and accessibility, and the transform is derived
     from where a card already sits. The one term that cannot carry over is the
     horizontal one: applying the fan's x on top of the scroll fights it and
     throws cards hundreds of pixels sideways. Depth, lift and turn do the work.

     Geometry is the engine's own formula and comes from CSS custom properties,
     so it is tunable per section without touching this file:
         theta = d * spread     z = (cos(theta) - 1) * radius
         y     = d * rise       tilt = d * tiltDeg      (rotateY, not a flat spin)
     y is signed on purpose: one neighbour drops and the other lifts, which is
     what makes spiral read as a spiral rather than as a shrink. */
  function kchMobileEffect(root, track) {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce && reduce.matches) return;
    const cards = Array.prototype.slice.call(track.querySelectorAll('.kch-card'));
    if (cards.length < 2) return;
    const cs = getComputedStyle(root);
    const num = (name, fb) => { const v = parseFloat(cs.getPropertyValue(name)); return isNaN(v) ? fb : v; };
    const g = {
      spread: num('--kch-m-spread', 0),
      radius: num('--kch-m-radius', 520),
      tilt:   num('--kch-m-tilt', 0),
      rise:   num('--kch-m-rise', 0),
      fade:   num('--kch-m-fade', 1)
    };
    /* nothing to do if the section is set to no effect on mobile */
    if (!g.spread && !g.tilt && !g.rise && g.fade === 1) return;
    const RAD = Math.PI / 180;
    let raf = 0;
    function paint() {
      raf = 0;
      const box = track.getBoundingClientRect();
      const padL = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const first = cards[0].getBoundingClientRect();
      const gap = cards.length > 1
        ? Math.max(0, cards[1].getBoundingClientRect().left - first.right)
        : 0;
      const step = first.width + gap;
      if (!step) return;
      /* the row is start aligned, so a snapped card rests against the left
         padding edge; that, not the middle of the track, is where d is zero */
      const rest = box.left + padL + first.width / 2;
      for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        const r = c.getBoundingClientRect();
        const d = ((r.left + r.width / 2) - rest) / step;
        const dc = Math.max(-2, Math.min(2, d));
        const theta = dc * g.spread * RAD;
        const z = (Math.cos(theta) - 1) * g.radius;
        const y = dc * g.rise;
        const tilt = dc * g.tilt;
        const a = Math.min(1, Math.abs(dc));
        c.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,' + z.toFixed(1) + 'px) rotateY(' + tilt.toFixed(2) + 'deg)';
        c.style.opacity = (1 - (1 - g.fade) * a).toFixed(3);
        c.style.zIndex = String(100 - Math.round(a * 100));
      }
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(paint); }
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    /* the section root carries the class the stylesheet keys the perspective off,
       so nothing paints in 3D until the effect is actually running */
    root.classList.add('kch-m-effect');
    paint();
  }

  function initCarousel(root) {
    /* Teardown registry. Every listener bound on window, document or a MediaQueryList
       registers its own removal here at the moment it is bound, and cleanup just drains
       the list.

       This exists because the previous arrangement bound listeners well before the line
       that assigned the cleanup which removed them, with three early returns in between.
       Any of those returns left a listener bound with nothing able to remove it, and it
       is unremovable for the life of the page. Registering at the point of binding means
       an early return can no longer strand anything, wherever it is added later.
       27 Jul. */
    root.__kchTeardown = root.__kchTeardown || [];
    const kchOwn = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      root.__kchTeardown.push(() => { try { target.removeEventListener(type, fn, opts); } catch (e) {} });
    };
    root.__kchCleanup = root.__kchCleanup || (() => {
      const list = root.__kchTeardown || [];
      while (list.length) { const f = list.pop(); try { f(); } catch (e) {} }
      root.__kchInit = false;
    });
    if (root.__kchInit) return;
    const stage = root.querySelector('[data-kch-stage]');
    const track = root.querySelector('[data-kch-track]');

  /* Concept-mode desktop drag: the stock slider-element has no pointer drag at all,
     verified in theme.js (buttons and native scroll only). This adds click-drag-to-
     scroll for mouse users on the concept slider, mirroring the hero's proven
     pointer-capture pattern. Touch stays native; snapping re-engages on release. */
  function kchConceptDrag(root) {
    const sl = root.querySelector('.kch-concept slider-element');
    if (!sl || sl.__kchDrag) return;
    sl.__kchDrag = true;
    let startX = 0, startLeft = 0, moved = 0, active = false;
    let dragging = false, suppressClick = false;
    sl.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      active = true; dragging = false; moved = 0; startX = e.clientX; startLeft = sl.scrollLeft;
      /* no capture yet: claiming the pointer here retargeted every click to the
         slider and killed the card links, Sean's 23 Jul dead-button report. The
         pointer is only captured once real movement makes this a drag. */
    });
    let lastX = 0, lastT = 0, vel = 0;
    sl.addEventListener('pointermove', (e) => {
      if (!active) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (!dragging) {
        if (Math.abs(dx) <= 4) return;
        dragging = true;
        sl.classList.add('kch-dragging');
        sl.setPointerCapture(e.pointerId);
      }
      const now = performance.now();
      if (lastT) {
        const dt = now - lastT;
        if (dt > 0) vel = (e.clientX - lastX) / dt;
      }
      lastX = e.clientX; lastT = now;
      sl.scrollLeft = startLeft - dx;
    });
    const end = (e) => {
      if (!active) return;
      active = false;
      if (!dragging) { vel = 0; lastT = 0; return; }
      dragging = false; suppressClick = true;
      sl.classList.remove('kch-dragging');
      if (sl.hasPointerCapture && sl.hasPointerCapture(e.pointerId)) sl.releasePointerCapture(e.pointerId);
      /* momentum glide, then settle exactly on the card pitch: base plus a whole
         number of pitches is origin-proof, unlike raw offsetLeft comparisons. */
      const projected = sl.scrollLeft - vel * 160;
      const grid = sl.querySelector('.card-grid');
      const items = grid ? Array.from(grid.children) : [];
      if (items.length > 1) {
        const base = items[0].offsetLeft;
        const pitch = items[1].offsetLeft - base;
        if (pitch > 0) {
          const target = base + Math.round((projected - base) / pitch) * pitch;
          const max = sl.scrollWidth - sl.clientWidth;
          sl.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
        }
      }
      vel = 0; lastT = 0;
    };
    sl.addEventListener('pointerup', end);
    sl.addEventListener('pointercancel', end);
    sl.addEventListener('click', (e) => {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; }
    }, true);
    sl.addEventListener('dragstart', (e) => e.preventDefault());
  }
  kchConceptDrag(root);


    /* Hover arms the arrow keys. Requiring a click first meant focusing the container,
       and focusing the container is what painted the outline; arrow keys are routed by
       pointer position instead, so nothing takes focus and no ring can appear. Keyboard
       users are unaffected: tabbing to the stage still focuses it and its own handler
       still runs, ring and all. Attached before the engine's early return, so Concept
       mode gets it too, where there is no stage at all. */
    var kchHover = false;
    root.addEventListener('pointerenter', function () { kchHover = true; });
    root.addEventListener('pointerleave', function () { kchHover = false; });
    function kchNav(dir) {
      if (root.__kchNav) { root.__kchNav(dir); return; }
      var sl = root.querySelector('slider-element');
      var first = root.querySelector('.kch-card');
      if (!sl) return;
      var step = first ? first.getBoundingClientRect().width + 16 : sl.clientWidth * 0.8;
      sl.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
    /* named so cleanup can remove it. document outlives the section, so an anonymous
       handler here cannot be removed at all and one accumulates per re-render.
       Shopify's guidance for shopify:section:unload is to clean up listeners so no
       memory leaks occur, which anonymity makes impossible.
       https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
       27 Jul. */
    const kchKeys = function (e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var st = root.querySelector('[data-kch-stage]');
      var focusedInside = st && (document.activeElement === st || st.contains(document.activeElement));
      if (!kchHover && !focusedInside) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'ArrowLeft' || e.key === 'Left') { e.preventDefault(); kchNav(-1); }
      else if (e.key === 'ArrowRight' || e.key === 'Right') { e.preventDefault(); kchNav(1); }
      else if (e.key === 'Home' && root.__kchHome) { e.preventDefault(); root.__kchHome(); }
      else if (e.key === 'End' && root.__kchEnd) { e.preventDefault(); root.__kchEnd(); }
    };
    kchOwn(document, 'keydown', kchKeys);
    root.__kchKeys = kchKeys;
    root.__kchKeysCapture = false;

    /* Below-the-card placement, for stock product cards as well as our collection
       cards. The stock card renders its buy button inside .product-card__media, and the
       media is the positioning ancestor, so no CSS can lift the button out of it: it
       sits centred at the foot of the image instead, which is what Sean is seeing in
       Cylinder and Spiral. The element is therefore moved in the DOM to be a direct
       child of the card, which is exactly where our own collection card puts it, so both
       card types then behave identically in every effect. Idempotent, and scoped to this
       section: the shared snippet is not touched. */
    (function () {
      if (!root.classList.contains('kch-ctaout')) return;
      var all = root.querySelectorAll('.product-card, .kch-collection-card');
      for (var i = 0; i < all.length; i++) {
        var cardEl = all[i];
        var qa = cardEl.querySelector('.quick-add');
        if (qa && qa.parentElement !== cardEl) cardEl.appendChild(qa);
      }
    })();

    if (!stage || !track) return;
    /* 23 Jul restructure: the engine is desktop-only. Under 1024px the section runs the
       native swipe row from the stylesheet; if the viewport later crosses up, init then. */
    const kchMq = window.matchMedia('(max-width: 1023px)');
    if (kchMq.matches) {
      const kchOnce = (e) => { if (!e.matches) { kchMq.removeEventListener('change', kchOnce); initCarousel(root); } };
      kchOwn(kchMq, 'change', kchOnce);
      /* the mobile branch returns before the main cleanup is defined, so kchOnce
         lives and dies here. It self-removes on crossing up, but a phone-width
         visitor who never crosses leaves it bound, and in the editor that is one
         more per re-render. 27 Jul. */
      root.__kchCleanup = () => { try { kchMq.removeEventListener('change', kchOnce); } catch (e) {} };
      /* optional mobile arrows: the buttons page the native swipe row by one card */
      if (root.classList.contains('kch-arrows-m')) {
        const prev = root.querySelector('[data-kch-prev]');
        const next = root.querySelector('[data-kch-next]');
        const page = (dir) => {
          const card = track.querySelector(':scope > *');
          const step = card ? card.getBoundingClientRect().width + 12 : track.clientWidth * 0.74;
          track.scrollBy({ left: dir * step, behavior: 'smooth' });
        };
        if (prev) prev.addEventListener('click', () => page(-1));
        if (next) next.addEventListener('click', () => page(1));
      }
      /* 27 Jul, Sean's call: same as the reviews section. Cylinder and spiral drop to
         the plain swipe row below 1024px, matching concept.

         Identical structure and identical fault: the engine owns position on desktop,
         while this path only decorated a native scroller it had no control over. The
         two cannot agree, so cards never land on their marks and the fan reshuffles
         after the scroll settles. Removed here at the same time rather than waiting
         for it to be reported separately, since the two sections share the design.

         kchMobileEffect stays in the file, unreferenced, and the CSS that keyed off
         .kch-m-effect has been removed with it. */
      return;
    }

    /* an overflow:hidden box still scrolls programmatically: a browser focus
       scroll or any scrollIntoView inside the stage shifts stage.scrollLeft and
       the whole coordinate system, arrows included, walks sideways with no way
       back. The engine owes its geometry to transforms alone, so the stage's
       own scroll stays pinned at zero. */
    stage.scrollLeft = 0;
    stage.scrollTop = 0;
    stage.addEventListener('scroll', function () { stage.scrollLeft = 0; stage.scrollTop = 0; });

    const cards = [...track.querySelectorAll('.kch-card')];
    if (!cards.length) return;
    root.__kchInit = true;

    const prevBtn = root.querySelector('[data-kch-prev]');
    const nextBtn = root.querySelector('[data-kch-next]');

    /* ---- geometry, read from the CSS custom properties on the root ---- */
    /* Fraction of a card's own width that must sit clear of its neighbour. 1.0 would
       mean edge to edge with no overlap at all; the fan is meant to overlap a little,
       and Sean's wireframe sits at about 1.07, so a shade under 1 keeps the layered
       look without the pile-up. */
    /* was a hardcoded 0.92; now a per-section setting, see --kch-clear. 25 Jul */
    const KCH_MIN_CLEAR = 0.92;

    const p = { spread: -18, radius: 680, tilt: 0, rise: 0, cardW: 260, zflat: 1, originX: 0.5, count: cards.length };
    function readGeometry() {
      const cs = getComputedStyle(root);
      function num(name, fb) { const v = parseFloat(cs.getPropertyValue(name)); return isNaN(v) ? fb : v; }
      p.spread = num('--kch-spread', -18);
      p.radius = num('--kch-radius', 680);
      p.tilt = num('--kch-tilt', 0);
      p.rise = num('--kch-rise', 0);
      /* unified cards size themselves to the concept column width in CSS, so the
         geometry reads the real rendered width and only falls back to the var */
      p.cardW = (typeof cards !== 'undefined' && cards[0] && cards[0].offsetWidth) || num('--kch-card-w', 260);
      p.zflat = num('--kch-zflat', 1);
      /* Publish the card's rendered width as a ratio of the 300px reference so CSS can
         scale anything that sits on the card: the rating badge, the quick-view eye and
         their inset from the corner. Without it those were fixed px and stayed full size
         as the card shrank, so at seven or eight columns they swamped the card and their
         corner padding disappeared. Sean 24 Jul. Recomputed on every geometry read, so it
         tracks the column count and the viewport. */
      root.style.setProperty('--kch-card-k', (p.cardW / 300).toFixed(3));
      /* Adaptive spread, 24 Jul. The neighbour sits at sin(spread) * radius, so the gap
         between card centres is fixed in degrees while the card width is not. At the
         cylinder preset, spread -18 and radius 680, neighbours land 210px apart; the
         carousel's cards are 300px, so they overlapped by 90px and the fan read as one
         crammed block. Reviews looked fine at the same preset only because its cards are
         250px. Sean's wireframe separates 278px against 260px cards and reads clean.

         So rather than a fixed angle, widen the spread until the neighbour clears the
         card by KCH_MIN_CLEAR of its own width. The preset is a floor, never a ceiling:
         a section whose cards are already well separated keeps exactly the angle it was
         given. Recomputed on every read, so it follows the card as the column count
         changes and cards shrink, which is the adaptive behaviour Sean asked for. */
      if (p.radius > 0 && p.cardW > 0) {
        /* Solve for the radius, not the angle. Card separation is sin(spread) * radius, so
           either term can deliver a requested spacing, but moving the angle changes the
           effect's character and stops working: past 90 degrees a cylinder wraps its cards
           back toward the centre, so the fan got NARROWER as the setting went up, and
           spiral's 400px radius hit that ceiling by 92 per cent and simply stopped
           responding. Measured both, 25 Jul. Holding the preset angle and scaling the
           radius keeps cylinder flat and spiral tilted, and the separation tracks the
           setting linearly across the whole range. */
        const clear = num('--kch-clear', KCH_MIN_CLEAR);
        const sin = Math.abs(Math.sin(p.spread * Math.PI / 180));
        if (sin > 0.01) p.radius = (p.cardW * clear) / sin;
      }
      p.originX = num('--kch-origin-x', 50) / 100;
      /* ported from the reviews carousel 22 Jul: rendering behaviour, per preset */
      p.grade = num('--kch-grade', 1);
      p.gradek = num('--kch-gradek', 55) / 100;
      p.wrap = num('--kch-wrap', 0);
      p.jitter = num('--kch-jitter', 0);
      p.back = num('--kch-back', 0);
      p.visible = Math.max(3, num('--kch-visible', 99));
    }
    readGeometry();

    /* ---- screen-reader status ---- */
    const statusEl = document.createElement('div');
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    root.appendChild(statusEl);
    const slideTpl = root.getAttribute('data-kch-status-template') || 'Item [x] of [y]';

    /* ---- shared state ---- */
    let pos = 0, target = 0, wheelVel = 0, lastActive = -1;
    /* Horizontal travel of the fan, 1 is the tuned geometry. fitLayout drops it only if
       the fan cannot otherwise clear the arrows at every stop. See the note there. */
    let fanFit = 1;
    let measuring = false;
    let startX = 0, startY = 0, startTarget = 0, dragging = false, dragged = false, t0 = 0, axis = null, activePointer = null, pointerKind = '', captured = false;

    const motionMql = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let reduceMotion = !!(motionMql && motionMql.matches);
    function onMotionChange(e) { reduceMotion = e.matches; }
    if (motionMql) { try { motionMql.addEventListener('change', onMotionChange); } catch (e) {} }

    function clamp(v) { return Math.max(0, Math.min(p.count - 1, v)); }
    function norm(a) { a = a % 360; if (a > 180) a -= 360; if (a < -180) a += 360; return a; }

    /* ---- the coverflow positioning, unchanged from the wireframe ---- */

    /* ---- motion. Concept moves its sliders with
       transform .6s cubic-bezier(.7, 0, .2, 1) (theme.css), an ease-in-out.
       This engine used exponential decay, which has no ease-in: every step left
       instantly and crawled in, which is the abruptness reported on the
       installed build. This is that curve, evaluated per frame. ---- */
    var KCH_EASE_MS = 600;
    var kchEase = (function () {
      var ax = 0.7, ay = 0.0, bx = 0.2, by = 1.0;
      function A(a, b) { return 1 - 3 * b + 3 * a; }
      function B(a, b) { return 3 * b - 6 * a; }
      function C(a) { return 3 * a; }
      function calc(t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
      function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
      return function (x) {
        if (x <= 0) return 0;
        if (x >= 1) return 1;
        var t = x, i, s, e;
        for (i = 0; i < 8; i++) {
          s = slope(t, ax, bx);
          if (!s) break;
          e = calc(t, ax, bx) - x;
          t -= e / s;
        }
        return calc(t, ay, by);
      };
    })();
    var twFrom = 0, twTo = 0, twT0 = 0, tweening = false;
    function startTween(to) { twFrom = pos; twTo = to; twT0 = 0; tweening = true; }

    /* fan limits. Past 90 degrees sine reverses and a card folds back into the
       fan, landing on or beside a live card. At spread 90 every fourth card
       returned to dead centre with an identical z-index, which is why a
       part-visible card took neither click nor drag. Holding the fan inside
       +-90 makes sin strictly monotonic in d, so no two cards can ever occupy
       the same point. KCH_MAX_FAN only binds at very small spreads. */
    var KCH_HALF_PI = Math.PI / 2;
    var KCH_MAX_FAN = 6;
    /* how far the fan may be eased in before card width takes over. 0.55 keeps the
       coverflow reading as a fan; below that it collapses toward a flat stack. */
    var KCH_FAN_FLOOR = 0.55;

    /* Scatter offsets are a function of the slot, not of the card, so the field is
       irregular to look at but identical at every position and cannot drift into a
       bunched arrangement as the deck advances. Continuous in d, so a drag stays smooth
       as cards change slots. */
    const kchOffX = (v) => Math.sin(v * 2.399 + 1.1);
    const kchOffY = (v) => Math.sin(v * 1.732 + 0.4);
    const kchOffS = (v) => Math.sin(v * 2.111 + 2.3);

    function render() {
      if (root.__kchSuspend) return;
      const spreadRad = p.spread * Math.PI / 180;
      const active = Math.round(pos);
      for (let i = 0; i < cards.length; i++) {
        // pos - i, not i - pos. That one flip mirrors x, rise and tilt
        // together so the next card enters from the trailing side and the
        // stack travels the way every other Concept slider travels. z uses
        // cosine and is unaffected.
        let d = pos - i;
        const theta = d * spreadRad;
        const x = Math.sin(theta) * p.radius * fanFit + (p.originX - 0.5) * stage.clientWidth;
        const z = (Math.cos(theta) - 1) * p.radius * (p.zflat || 1);
        const y = d * p.rise;
        const tilt = d * p.tilt;
        const card = cards[i];
        /* No front-card magnification. The reviews cards keep their 12 per cent lift,
           but here the active card must be the collection card at true size: with the
           bump, every swatch, button and badge on the front card rendered 12 per cent
           larger than the featured collection's, which is the mismatch Sean kept
           flagging. Echo scale still comes from perspective and jitter. */
        let cx = x, cy = y, sc = 1;
        if (p.jitter) {
          const jk = Math.min(Math.abs(d), 1);
          cx += kchOffX(d) * p.jitter * jk;
          cy += kchOffY(d) * p.jitter * 1.2 * jk;
          sc *= 1 + kchOffS(d) * 0.12 * jk;
        }
        /* the cards stack in one grid cell, so the origin is already their centre */
        card.style.transform = `translate3d(${cx.toFixed(1)}px,${cy.toFixed(1)}px,${z.toFixed(1)}px) rotateY(${tilt.toFixed(2)}deg) scale(${sc.toFixed(3)})`;
        const na = Math.abs(norm(tilt));
        /* The fold gate keeps sine monotonic so no two cards can share a point. Scatter
           wants the opposite: past 90 degrees the orbit wraps and the card three out
           swings back to the far side, which is the scattered read. Its window stops
           before the card four out, which returns to dead centre behind the active one. */
        const folded = Math.abs(d) > KCH_MAX_FAN || (!p.wrap && Math.abs(theta) > KCH_HALF_PI + 1e-6);
        /* Continuous fade rather than a binary cull. The 0/1 switch popped cards in and
           out at the angle and depth thresholds, which read as a flicker between cards
           and left a sliver at the far edge. */
        const fadeA = Math.max(0, Math.min(1, (90 - na) / 12));
        const fadeZ = Math.max(0, Math.min(1, (z + p.radius * 1.15) / (p.radius * 0.18)));
        const half = (p.visible - 1) / 2;
        const lim = (d > 0 && p.back) ? p.back : half;
        const fadeV = Math.max(0, Math.min(1, (lim + 0.5 - Math.abs(d)) / 0.5));
        const gk = Math.min(Math.abs(d), 1);
        /* grade 1 opacity crossfade, grade 3 wash: the card keeps full opacity and takes
           a sheet of the page colour instead, so flat-stacked cards never show through
           one another. The front swap crossfades either way. */
        /* The recede is the wireframe's own: a gentle opacity step per slot out from
           the front, 16 percent each, floored at 12 percent, with no filter, no wash
           and no overlay. One slot back reads at 0.84, which is a fade rather than a
           see-through, which is why nothing behind a card shows through it. Ported
           verbatim from kch-carousel-wireframe-v54, line 662. */
        card.style.filter = '';
        const cull = folded ? 0 : Math.min(fadeA, fadeZ, fadeV);
        const recede = Math.max(0.12, 1 - Math.abs(d) * 0.16);
        /* The card stays opaque and recedes by wash, so a stacked fan never shows one
           card through another. Opacity is left to the cull at the window edge. */
        card.style.opacity = cull.toFixed(3);
        card.style.setProperty('--kch-wash', (1 - recede).toFixed(3));
        /* Shadow strength tapers with depth. Every card keeps a shadow, but a card two
           slots back casts at a fraction of the front card's, so where the fan overlaps
           the seams never compound into the dark band Sean saw. Squaring the depth term
           makes the fall-off faster than the opacity fade, which is what stops the
           stack reading as one heavy shadow. */
        const depth = Math.max(0, 1 - Math.abs(d) * 0.28);
        card.style.setProperty('--kch-shadow-k', (depth * depth).toFixed(3));
        /* only the front card is live: buttons, links and hover exist nowhere else */
        card.style.pointerEvents = (!folded && Math.abs(d) < 0.5) ? 'auto' : 'none';
        card.style.zIndex = String(1000 + Math.round(z));
      }
      if (!measuring && active !== lastActive) {
        for (let k = 0; k < cards.length; k++) {
          const off = (k !== active);
          if (off) { cards[k].setAttribute('aria-hidden', 'true'); } else { cards[k].removeAttribute('aria-hidden'); }
          cards[k].classList.toggle('kch-card--active', !off);
          if (off) cards[k].classList.remove('kch-card--show-dots');
          if (off && cards[k].contains(document.activeElement)) {
            // never leave focus trapped inside an aria-hidden card
            /* Our own move, not the shopper tabbing. Chrome treats a programmatic focus()
               as focus-visible, so the stage painted a 2px ring that showed up in Sean's
               screenshots as a border round the section. Flagging it as pointer-driven
               keeps the ring for genuine Tab navigation and drops it here. 25 Jul. */
            stage.setAttribute('data-kch-pointer', '');
            try { stage.focus({ preventScroll: true }); } catch (fe) { stage.focus(); }
          }
          [...cards[k].querySelectorAll('a, button, select, input, [tabindex]')].forEach((elc) => {
            if (elc.closest('[data-kch-gallery]') || elc.closest('quick-view')) return;
            if (off) { elc.setAttribute('tabindex', '-1'); } else { elc.removeAttribute('tabindex'); }
          });
          if (cards[k]._kchResetGallery) cards[k]._kchResetGallery();
        }
        const lblEl = cards[active]?.querySelector('.kch-card__title');
        statusEl.textContent = slideTpl.replace('[x]', active + 1).replace('[y]', cards.length) + (lblEl ? ': ' + lblEl.textContent.trim() : '');
        if (prevBtn) prevBtn.hidden = active <= 0;
        if (nextBtn) nextBtn.hidden = active >= cards.length - 1;
        lastActive = active;
      }
    }

    /* ---- animation loop, unchanged pacing ---- */
    let raf = null, running = false, lastT = 0;
    function frame(ts) {
      if (!lastT) lastT = ts;
      var dtn = Math.min(ts - lastT, 100) / 16.667;
      lastT = ts;
      if (!dragging) {
        if (wheelVel) {
          // a live wheel gesture follows the input directly; the tween takes
          // over once the gesture dies and the carousel snaps to a card
          tweening = false;
          var nt = clamp(target + wheelVel);
          if (nt === target) wheelVel = 0; else target = nt;
          wheelVel *= Math.pow(0.88, dtn);
          if (Math.abs(wheelVel) < 0.0006) { wheelVel = 0; target = clamp(Math.round(target)); startTween(target); }
          else { pos += (target - pos) * (1 - Math.pow(0.80, dtn)); }
        } else if (tweening) {
          if (!twT0) twT0 = ts;
          var k = Math.min(1, (ts - twT0) / KCH_EASE_MS);
          pos = twFrom + (twTo - twFrom) * kchEase(k);
          if (k >= 1) { pos = twTo; tweening = false; }
        } else if (Math.abs(target - pos) > 0.0008) {
          startTween(target);
        }
      }
      var settled = !dragging && !wheelVel && !tweening && Math.abs(target - pos) < 0.0008;
      if (settled) { pos = target; }
      render();
      if (settled) { running = false; raf = null; return; }
      raf = requestAnimationFrame(frame);
    }
    function kick() {
      if (reduceMotion) { wheelVel = 0; pos = target; render(); return; }
      if (!running) { running = true; lastT = 0; raf = requestAnimationFrame(frame); }
    }
    function setTarget(v) { target = clamp(v); if (!reduceMotion) startTween(target); kick(); }



    /* The horizontal counterpart of fitStage. The arrows own a no-go zone at each end
       of the stage; the cards must lay out entirely between them and must never be
       clipped by the stage edge. This measures the rendered row and shrinks the card
       width until the whole layout fits, so the result holds for any card count, any
       effect, any ratio and either card style rather than for one tuned case. */
    /* Structurally zero since 25 Jul. The arrows used to sit inside the stage, so the fan
       had to shrink to keep cards from sliding under them, and this measured how much to
       hold back. They now live outside the stage, pinned into padding the card area
       reserves (.kch-cardarea, kch-product-carousel.css), so the stage's whole width is
       the fan's to use and clearance is guaranteed by the layout rather than by shrinking.

       Kept as a function rather than deleted so the two callers keep reading as an
       intentional zero, and so a future arrow-inside-the-stage variant has one place to
       restore. It was already returning 0 after the move, but by selector miss rather
       than by design, which is not a thing to leave in place. */
    function arrowGutter() {
      return 0;
    }

    function unionBox() {
      let l = Infinity, r = -Infinity;
      for (let i = 0; i < cards.length; i++) {
        const b = cards[i].getBoundingClientRect();
        if (!b.width || parseFloat(cards[i].style.opacity || '1') < 0.02) continue;
        if (b.left < l) l = b.left;
        if (b.right > r) r = b.right;
      }
      return isFinite(l) ? { l: l, r: r } : null;
    }

    /* The row must clear the arrows at every scroll position, not only at rest. In an
       orbit the extents grow as cards swing past the front, and at the two ends the
       whole fan lies to one side, so a fit measured at the resting position alone was
       always going to let a card slide under an arrow mid-scroll, which is what Sean
       saw. This walks every stop plus the half-steps between them, keeps the widest
       row any of them produce, and fits to that. */
    /* How far the drawn row pushes past either arrow gutter, at the worst stop.

       This replaced a width-only test on 24 Jul. The old test compared the row's WIDTH
       against the room between the arrows, which is only meaningful if the row is
       centred in the stage, and at the two ends of the stack it is not: the whole fan
       lies to one side of the front card. Measured on a 1200px stage with three cards,
       the row was 814px wide against 1008px of room, so the width test passed happily
       while the outermost card still sat 48px inside the left gutter. That is the card
       tucked behind the arrow Sean photographed at the end of the stack.

       Edges, not width. 28px of shadow margin is held back on each side for the same
       reason fitLayout does: a card flush to the clip shows its shadow sliced. */
    function worstOverflow() {
      const saved = pos;
      const n = cards.length;
      const g = arrowGutter();
      const sr = stage.getBoundingClientRect();
      const limL = sr.left + g + 28;
      const limR = sr.right - g - 28;
      let worst = 0;
      measuring = true;
      for (let i = 0; i < n; i++) {
        for (const off of (i < n - 1 ? [0, 0.5] : [0])) {
          pos = i + off;
          render();
          const b = unionBox();
          if (!b) continue;
          const over = Math.max(limL - b.l, b.r - limR);
          if (over > worst) worst = over;
        }
      }
      pos = saved;
      render();
      measuring = false;
      return worst;
    }

    /* Fit the drawn row between the arrows at every stop.

       Two levers, tried in this order, because they cost different things.

       1. Fan travel. The horizontal spread comes from sin(theta) * radius, so it is a
          constant of the geometry and does not change with card size. On a 1200px stage
          it reaches roughly 1360px of span against 1008px of usable room, so with five
          or more cards no amount of card shrinking can ever clear the arrows: measured,
          the row still overhung by 25px with the cards driven all the way down to the
          120px floor. Tightening the travel is the only lever that works at every card
          count, and it leaves card size alone, which matters because the front card is
          meant to render at the same size as the featured collection's.

       2. Card width, unchanged from before, as the fallback. It is enough on its own for
          a short stack, three cards cleared at 301px, but it costs a third of the card,
          so it is only reached if the fan is already at its floor and the row still
          overhangs, which happens on genuinely narrow stages.

       Both are adaptive and both start from the tuned values. Where the geometry already
       fits, which is any reasonably wide desktop, fanFit stays at 1 and no card-fit is
       written, so the tuned look is untouched. Narrower stages tighten only as far as
       they must. */
    function fitLayout() {
      if (!cards.length || root.__kchSuspend) return;
      fanFit = 1;
      root.style.removeProperty('--kch-card-fit');
      readGeometry();
      render();
      const base = cards[0].offsetWidth;
      if (!base) return;

      /* lever 1: ease the fan in, 5 per cent at a time, no further than the floor */
      for (let pass = 0; pass < 10; pass++) {
        if (worstOverflow() <= 0.5) return;
        if (fanFit <= KCH_FAN_FLOOR) break;
        fanFit = Math.max(KCH_FAN_FLOOR, Math.round((fanFit - 0.05) * 100) / 100);
        render();
      }

      /* lever 2: still overhanging with the fan at its floor, so trim the cards.
         Each pixel off the card buys well under a pixel of clearance, because only the
         outermost card's own half-width moves, so this measures and re-measures rather
         than assuming a ratio. */
      let w = base;
      for (let pass = 0; pass < 6; pass++) {
        const over = worstOverflow();
        if (over <= 0.5) break;
        const next = Math.max(120, Math.floor(w - over * 2.2));
        if (next === w) break;
        w = next;
        root.style.setProperty('--kch-card-fit', w + 'px');
        readGeometry();
        render();
      }
    }

    /* Fit the stage to what the engine actually drew. Centring the cards in a
       formula-sized box left dead space above them, so the section title sat far
       further from the cards than it does in Concept mode, and card style made it
       worse by splitting the hang allowance top and bottom. This measures the union
       of the rendered card boxes (transforms, scale, rise and jitter included) and
       sets the height and the card anchor from it, so the gap above the cards is a
       fixed 8px in every effect and the hang is reserved only below. Sean's 23 Jul
       proximity call; measured, not derived. */

    /* ---- per-card wiring ---- */
    cards.forEach((card, idx) => {
      function isActive() { return Math.round(pos) === idx; }

      /* unified cards carry the stock title class; the bespoke one went with the
         23 Jul unification, which left every card announced with no name */
      const ttl = card.querySelector('.product-card__title, .kch-card__title');
      const slideLbl = slideTpl.replace('[x]', idx + 1).replace('[y]', cards.length);
      card.setAttribute('aria-label', ttl ? `${slideLbl}: ${ttl.textContent.trim()}` : slideLbl);

      // active-card photo opens the quick view (only on the bare media, not a control)
      card.addEventListener('click', (e) => {
        if (dragged || !isActive()) return;
        if (e.target.closest('a, button, input, label, select, .kch-card__dots-tab')) return;
        /* same reason: the stock card's quick-view trigger, so tapping the front
           card's photo opens quick view again */
        const eye = card.querySelector('.quick-view__button[aria-controls], .kch-card__eye[aria-controls]');
        if (eye) eye.click();
      });

      // hover image gallery: the photo tracks the cursor across the card, so
      // moving across the width steps through the shots (1/N of the width per
      // shot). The crossfade is the slow, smooth 0.5s set in CSS, which is what
      // takes the edge off the old snappy feel.
      const gallery = card.querySelector('[data-kch-gallery]');
      if (!gallery) return;
      const shots = [...gallery.querySelectorAll('.kch-card__shot')];
      const dots = [...card.querySelectorAll('[data-kch-dot]')];
      if (shots.length < 2) return;
      let curShot = 0;
      function showShot(n) {
        n = Math.max(0, Math.min(shots.length - 1, n));
        if (n === curShot) return;
        curShot = n;
        for (let sI = 0; sI < shots.length; sI++) shots[sI].classList.toggle('is-active', sI === n);
        for (let dI = 0; dI < dots.length; dI++) dots[dI].classList.toggle('is-active', dI === n);
      }
      card._kchResetGallery = () => { showShot(0); };
      // Confine the scrub to the clean image above the add-to-cart button. Past
      // the button, down to the dots, the cursor crosses the button and dot
      // overlays, which interrupts the scrub and strobes; hold the photo there
      // instead. Mirrors Concept keeping the slideshow on the image only.
      let scrubMaxY = Infinity;
      gallery.addEventListener('mouseenter', () => {
        if (isActive() && !dragging) card.classList.add('kch-card--show-dots');
        const cta = card.querySelector('.kch-card__quickadd');
        const cr = cta?.getBoundingClientRect();
        scrubMaxY = cr?.height ? cr.top : Infinity;
      });
      gallery.addEventListener('mousemove', (e) => {
        if (dragging || !isActive()) return;
        if (e.clientY >= scrubMaxY) return;
        const r = gallery.getBoundingClientRect();
        if (!r.width) return;
        let frac = (e.clientX - r.left) / r.width;
        if (frac < 0) frac = 0; else if (frac > 0.9999) frac = 0.9999;
        showShot(Math.floor(frac * shots.length));
      });
      gallery.addEventListener('mouseleave', () => { card.classList.remove('kch-card--show-dots'); showShot(0); });
      // Dots are indicators only, like Concept's: they reflect the current photo
      // but do not drive it. Concept's sit inside the product link, so hovering
      // does nothing and a click just navigates away; ours simply stay inert.
    });

    // capture-phase guard: a click on an inactive card brings it forward and is
    // swallowed before any inner control (quick-view trigger, link) can fire; a
    // click right after a drag is swallowed too
    track.addEventListener('click', (e) => {
      if (dragged) { e.preventDefault(); e.stopPropagation(); return; }
      const card = e.target.closest('.kch-card');
      if (!card) return;
      const ci = cards.indexOf(card);
      if (ci < 0) return;
      if (Math.round(pos) !== ci) { e.preventDefault(); e.stopPropagation(); setTarget(ci); }
    }, true);

    /* The stage is focusable, and the engine focuses it when the active card changes.
       A screenshot shortcut is a key press, so the browser flips to keyboard modality
       and paints the focus ring on the already-focused stage: the green box Sean sees
       only in screenshots. The ring is now gated on real keyboard intent. */
    stage.addEventListener('pointerdown', () => { stage.setAttribute('data-kch-pointer', ''); });
    root.__kchNav = function (dir) { setTarget(clamp(Math.round(target) + dir)); };
    stage.addEventListener('keydown', (e) => {
      /* same rule for the stage's own ring: Tab reveals it, arrows do not */
      if (e.key === 'Tab') stage.removeAttribute('data-kch-pointer');
    });

    /* ---- keyboard ----
       The stage used to carry its own arrow handler as well as the hover-armed one on
       the document. With the pointer over the section and the stage focused, both fired
       and the carousel moved two cards per press. There is now one handler, and it
       accepts either condition: pointer over the section, or focus inside the stage. */
    root.__kchHome = () => setTarget(0);
    root.__kchEnd = () => setTarget(clamp(cards.length - 1));

    /* ---- spatial helpers ---- */
    function nearCard(x, y) {
      let L = Infinity, R = -Infinity, T = Infinity, B = -Infinity, any = false;
      for (let i = 0; i < cards.length; i++) {
        if (parseFloat(cards[i].style.opacity || '1') < 0.05) continue;
        const r = cards[i].getBoundingClientRect();
        any = true;
        if (r.left < L) L = r.left;
        if (r.right > R) R = r.right;
        if (r.top < T) T = r.top;
        if (r.bottom > B) B = r.bottom;
      }
      return any && x >= L && x <= R && y >= T && y <= B;
    }
    function guard(t) { return t.closest('.kch-arrow') || t.closest('.kch-card__dots-tab') || t.closest('.kch-card__variants'); }

    /* ---- wheel: intentionally none ----
       Removed on Sean's call, 22 Jul, matching the reviews carousel. A wheel-driven
       carousel traps the page scroll inside the section and frustrates more than it
       helps. Drag, arrows and keyboard remain the inputs. wheelVel stays declared and
       is simply never fed, so the tween loop below needs no change. */

    /* ---- drag (pointer for the maths, touch only to cancel native scroll) ---- */
    function down(xx, yy) { startX = xx; startY = yy; startTarget = target; dragging = true; dragged = false; axis = null; captured = false; t0 = Date.now(); }
    function move(xx, yy) {
      if (!dragging) return;
      const dx = xx - startX, dy = yy - startY;
      if (!axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        // thumbs arc, so a swipe that starts slightly vertical is still a swipe
        axis = (pointerKind === 'mouse') ? 'x' : (Math.abs(dx) >= Math.abs(dy) * 0.75 ? 'x' : 'y');
        if (axis === 'y') { dragging = false; return; }
      }
      if (axis === 'x' && !captured) {
        captured = true;
        try { stage.setPointerCapture(activePointer); } catch (err) {}
      }
      if (axis === 'x') {
        dragged = true;
        // minus, not plus: with the mirrored placement (d = pos - i) the stack
        // follows the finger only when a rightward drag lowers the target
        let raw = startTarget - (xx - startX) / Math.max(160, p.cardW), maxT = cards.length - 1;
        if (raw < 0) raw *= 0.25; else if (raw > maxT) raw = maxT + (raw - maxT) * 0.25;
        setTarget(raw);
      }
    }
    function up() {
      if (!dragging) return;
      dragging = false;
      const moved = target - startTarget;
      const quick = (Date.now() - t0) < 260;
      if (Math.abs(moved) < 0.5 && (Math.abs(moved) > 0.16 || (Math.abs(moved) > 0.06 && quick))) {
        setTarget(clamp(Math.round(startTarget) + (moved > 0 ? 1 : -1)));
      } else { setTarget(clamp(Math.round(target))); }
      setTimeout(() => { dragged = false; }, 0);
      axis = null;
    }
    function endPointer(e) { if (activePointer === null || e.pointerId !== activePointer) return; activePointer = null; up(); }

    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!nearCard(e.clientX, e.clientY)) return;
      if (activePointer !== null) return;
      if (guard(e.target)) return;
      activePointer = e.pointerId;
      pointerKind = e.pointerType || 'mouse';
      down(e.clientX, e.clientY);
    });
    function onPointerMove(e) { if (e.pointerId === activePointer) move(e.clientX, e.clientY); }
    kchOwn(window, 'pointermove', onPointerMove);
    kchOwn(window, 'pointerup', endPointer);
    kchOwn(window, 'pointercancel', endPointer);

    stage.addEventListener('touchstart', () => {}, { passive: false });
    stage.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) { if (dragging) { dragging = false; axis = null; } return; }
      if (!dragging && axis !== 'x') return;
      if (axis === null) {
        const tdx = Math.abs(e.touches[0].clientX - startX), tdy = Math.abs(e.touches[0].clientY - startY);
        if (!tdx && !tdy) return;
        axis = tdx > tdy ? 'x' : 'y';
        if (axis === 'y') { dragging = false; return; }
      }
      if (axis === 'x') { e.preventDefault(); }
    }, { passive: false });

    /* ---- a click in the empty space beside the cards steps toward that side ---- */
    stage.addEventListener('click', (e) => {
      if (dragged) return;
      if (guard(e.target) || e.target.closest('.kch-card')) return;
      if (!nearCard(e.clientX, e.clientY)) return;
      const r = stage.getBoundingClientRect();
      // sign-aware: with negative spread the left neighbour is the higher
      // index, with positive spread (Scatter) it is the lower one
      setTarget(clamp(Math.round(target) + (e.clientX < r.left + r.width / 2 ? -1 : 1) * (p.spread < 0 ? 1 : -1)));
    });
    stage.addEventListener('dragstart', (e) => { e.preventDefault(); });

    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); setTarget(clamp(Math.round(target) - 1)); });
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); setTarget(clamp(Math.round(target) + 1)); });

    /* The stage is sized by the track, which is one real card tall. This publishes only
       the room the effects need around it, read from the boxes the browser paints, which
       is the same thing the inline pass does at parse time. One pass, no correction. */
    function readExtents(h, trackTop, acc) {
      for (var i = 0; i < cards.length; i++) {
        if (parseFloat(cards[i].style.opacity || '1') < 0.02) continue;
        var r = cards[i].getBoundingClientRect();
        var relTop = r.top - trackTop;
        var relBot = r.bottom - trackTop - h;
        if (relTop < acc.up) acc.up = relTop;
        if (relBot > acc.down) acc.down = relBot;
      }
    }

    function publishExtents() {
      if (!cards.length || root.__kchSuspend) return;
      var h = cards[0].offsetHeight;
      if (!h) return;
      var trackTop = track.getBoundingClientRect().top;
      var acc = { up: 0, down: 0 };
      readExtents(h, trackTop, acc);
      /* Worst case across the scroll, not just at rest. A card's painted height depends
         only on its distance from the front, and its offset is that distance times rise,
         so the extreme falls at the half-step between stops. Both are laid out and read
         here, in the same synchronous block, and the resting layout is restored before
         anything is painted. */
      /* The extreme sits somewhere between two stops, and it is not always the midpoint:
         which card is culled changes as the row turns, so the union grows and shrinks
         unevenly across the step. Sampling quarters covers the continuum closely enough
         that nothing clipped anywhere across a full scroll of every card. */
      var saved = pos;
      measuring = true;
      var offsets = [0.25, 0.5, 0.75];
      for (var q = 0; q < offsets.length; q++) {
        pos = saved + offsets[q];
        render();
        readExtents(h, track.getBoundingClientRect().top, acc);
      }
      pos = saved;
      render();
      measuring = false;
      /* Sean 25 Jul: cap the section rather than grow it around the fan.
       *
       * This used to reserve room for the widest extent any card reached anywhere in a
       * full scroll, so spiral, whose rise throws one side high, opened a gap above the
       * cards that concept and cylinder never had. That gap was the only reason a
       * spacing control was needed at all: three effects, three different distances
       * from the heading, and no way to make them agree.
       *
       * The section is now sized for the front card alone. The front card is the one
       * that must be whole; the cards behind it are context and may run past the edge.
       * The stage is already overflow:hidden, so they clip cleanly. Every effect now
       * measures the same from the heading, on every device, with no tuning.
       *
       * Top is a fixed 8px of breathing room. Bottom follows the front card, because
       * its title, price and swatches sit in flow beneath the media and have to fit. */
      var front = track.querySelector('.kch-card--active') || cards[0];
      var frontTop = 0, frontBottom = 0;
      if (front) {
        var fr = front.getBoundingClientRect();
        var tt = track.getBoundingClientRect().top;
        /* negative when the card's painted top sits above the track line, which it does
           whenever the active card is scaled up: scale works from the centre, so a card
           at 1.12 lifts its own top by six per cent of its height. A flat 8px ignored
           that and cut the front card's photo, which Sean caught on cylinder where
           nothing else moves vertically at all. */
        frontTop = fr.top - tt;
        frontBottom = fr.bottom - tt - h;
      }
      /* The front card is the one that must be whole, so both paddings follow it and
         nothing else. Cards behind may still run past the edge and clip, which is the
         point: the section stays the same height in every effect. EDGE is the breathing
         room beyond the front card, a little more than concept's zero so a scaled or
         tilted card never touches the frame. */
      var EDGE = 16;
      stage.style.setProperty('--kch-pad-top', Math.ceil(Math.max(0, -frontTop) + EDGE) + 'px');
      stage.style.setProperty('--kch-pad-bottom', Math.ceil(Math.max(0, frontBottom) + EDGE) + 'px');
    }

    /* ---- resize ---- */
    let rzTimer = null;
    function onResize() {
      clearTimeout(rzTimer);
      rzTimer = setTimeout(() => { fitLayout(); render(); publishExtents(); }, 150);
    }
    kchOwn(window, 'resize', onResize);

    /* ---- cleanup (theme editor section reloads) ---- */
    root.__kchCleanup = () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endPointer);
      window.removeEventListener('pointercancel', endPointer);
      window.removeEventListener('resize', onResize);
      if (motionMql) { try { motionMql.removeEventListener('change', onMotionChange); } catch (e) {} }
      /* bound to objects that outlive the section, so without these every editor
         re-render leaves another copy behind holding a dead root. The pointer and
         resize handlers above were already covered; these three were not. 27 Jul. */
      try { kchMq.removeEventListener('change', kchCross); } catch (e) {}
      if (root.__kchKeys) document.removeEventListener('keydown', root.__kchKeys, root.__kchKeysCapture);
      root.__kchInit = false;
    };

    /* ---- boot: upgrade to 3D, then position before first paint ---- */
    pos = target = Math.floor((cards.length - 1) / 2);
    /* The section already painted its finished layout during parse, and the ready class
       is already on. Nothing is hidden and nothing is waiting, so there is no reveal to
       schedule and no timer to guard it. */
    root.classList.add('kch-carousel--ready');
    /* crossing down to phone width after init: park the engine and hand the cards back
       to the stylesheet's swipe row; crossing back up resumes in place. */
    /* named, not inline, so cleanup can remove it. kchMq is a MediaQueryList and
       outlives the section DOM, so an anonymous handler survives every editor
       re-render holding a dead root. 27 Jul. */
    const kchCross = (e) => {
      if (e.matches) {
        root.__kchSuspend = true;
        root.classList.remove('kch-carousel--ready');
        stage.style.height = ''; root.style.removeProperty('--kch-card-fit');
        cards.forEach((c) => {
          c.style.transform = ''; c.style.opacity = ''; c.style.zIndex = '';
          c.style.filter = '';
          c.removeAttribute('aria-hidden');
        });
      } else {
        root.__kchSuspend = false;
        root.classList.add('kch-carousel--ready');
        render();
      }
    };
    kchOwn(kchMq, 'change', kchCross);
    /* re-read after the class lands: any geometry variable scoped to --ready in
       the stylesheet is not matching during the readGeometry() call above.
       Mirrors relayout() in kch-reviews.js. */
    requestAnimationFrame(() => {
      fitLayout(); render(); publishExtents();
      /* Reveal only once nothing is still moving. Fonts and images change card size
         after first paint, and the fit follows them, so revealing before they land shows
         the adjustment happening. Same approach as the reviews engine; the 900ms cap and
         the 600ms safety timer above bound the wait. */
      fitLayout(); render(); publishExtents();
    });
    kick();
    try { initVariants(root); } catch (e) { if (window.console) console.warn('[kch-carousel] variant init skipped:', e); }
  }

  /* ---- variant swatches / dropdown on each card ---- */
  function initVariants(root) {
    const wraps = root.querySelectorAll('[data-kch-variants]');
    [...wraps].forEach((wrap) => {
      const card = wrap.closest('.kch-card');
      if (!card) return;
      const single = wrap.getAttribute('data-single-option') === 'true';
      const addText = wrap.getAttribute('data-add-text') || 'Add to cart';
      const soldText = wrap.getAttribute('data-sold-text') || 'Sold out';
      const form = card.querySelector('.kch-card__addform');
      const idInput = form ? form.querySelector('[data-kch-variant-id]') : null;
      const addBtn = form ? form.querySelector('.kch-card__quickadd-btn') : null;
      const addLabel = addBtn ? addBtn.querySelector('[data-kch-add-label]') : null;
      const foot = card.querySelector('.kch-card__foot');

      function swapImage(url) {
        if (!url) return;
        const shots = card.querySelectorAll('.kch-card__shot');
        if (!shots.length) return;
        for (let i = 0; i < shots.length; i++) shots[i].classList.toggle('is-active', i === 0);
        const img0 = shots[0].querySelector('img');
        if (img0) { img0.removeAttribute('srcset'); img0.setAttribute('src', url); }
        const dots = card.querySelectorAll('[data-kch-dot]');
        for (let d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === 0);
      }
      function setPrice(price, cprice) {
        // single-option products only: on a multi-option product the first
        // option does not pin down one variant, so a swapped price could be
        // wrong; those cards keep Concept's rendered price untouched
        if (!single || !foot || !price) return;
        let h = '<div class="price">';
        if (cprice) h += `<s>${cprice}</s> `;
        h += `<span class="price__regular whitespace-nowrap">${price}</span></div>`;
        foot.innerHTML = h;
      }
      function setAdd(available) {
        if (!single || !addBtn) return;
        if (available === 'false') {
          addBtn.setAttribute('disabled', '');
          if (addLabel) addLabel.textContent = soldText;
        } else {
          addBtn.removeAttribute('disabled');
          if (addLabel) addLabel.textContent = addText;
        }
      }
      function select(d) {
        if (single && idInput && d.id) idInput.value = d.id;
        swapImage(d.image);
        setPrice(d.price, d.cprice);
        setAdd(d.available);
        const live = wrap.querySelector('[data-kch-live]');
        if (live) live.textContent = (d.price || '') + (d.available === 'false' ? ', ' + (wrap.getAttribute('data-sold-text') || 'Sold out') : '');
      }

      const swatches = wrap.querySelectorAll('[data-kch-swatch]');
      [...swatches].forEach((sw) => {
        sw.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (sw.getAttribute('data-available') === 'false') return;
          for (let i = 0; i < swatches.length; i++) { swatches[i].classList.remove('is-selected'); swatches[i].setAttribute('aria-pressed', 'false'); }
          sw.classList.add('is-selected');
          sw.setAttribute('aria-pressed', 'true');
          select({ id: sw.getAttribute('data-variant-id'), image: sw.getAttribute('data-image'), price: sw.getAttribute('data-price'), cprice: sw.getAttribute('data-cprice'), available: sw.getAttribute('data-available') });
        });
      });

      const sel = wrap.querySelector('[data-kch-variant-select]');
      if (sel) {
        sel.addEventListener('change', () => {
          const o = sel.options[sel.selectedIndex];
          select({ id: o.getAttribute('data-variant-id'), image: o.getAttribute('data-image'), price: o.getAttribute('data-price'), cprice: o.getAttribute('data-cprice'), available: o.getAttribute('data-available') });
        });
        sel.addEventListener('click', (e) => { e.stopPropagation(); });
      }
    });
  }

  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-kch-carousel]');
    [...roots].forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor lifecycle
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-kch-carousel]');
    if (root && root.__kchCleanup) root.__kchCleanup();
  });
})();
